import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import dayjs from "@/lib/bizday";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { BookingModal, deriveVertical } from "@/pages/Bookings";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { StaffPlanDialog } from "@/components/pawkt/StaffPlan";

// Calendar entries: one per staffed service block (actual staff time); legacy bookings keep one entry.
const entries = (items) => items.flatMap((b) => {
  const rows = (b.assignments || []).filter((a) => a.status !== "cancelled");
  if (!rows.length) return [{ key: b.id, b, start: b.start_time, end: b.end_time, label: b.service_name, staff: b.staff_name }];
  return rows.map((a) => ({ key: a.id, b, a, start: a.start_time, end: a.end_time, label: a.service_name, staff: (a.staff_names || []).join(", ") || "Unassigned", conflict: !!a.override }));
});

const DEFAULT_FROM = 8, DEFAULT_TO = 19; // default window 8am–7pm; auto-extends to any booked hour
const STATUS_BG = {
  confirmed: "bg-emerald-100 border-emerald-300 text-emerald-800",
  pending: "bg-amber-100 border-amber-300 text-amber-800",
  in_progress: "bg-sky-100 border-sky-300 text-sky-800",
  completed: "bg-slate-100 border-slate-300 text-slate-700",
  cancelled: "bg-rose-50 border-rose-200 text-rose-500 line-through",
  no_show: "bg-rose-50 border-rose-200 text-rose-500",
};

export default function CalendarPage() {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState(dayjs());
  const [open, setOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [plan, setPlan] = useState(null);
  const { user } = useAuth();
  const canCreate = hasPerm(user, "bookings.create");

  const weekStart = anchor.startOf("week");
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
  const start = weekStart.format("YYYY-MM-DDT00:00:00+05:30");
  const end = weekStart.add(7, "day").format("YYYY-MM-DDT00:00:00+05:30");

  const { data, isLoading } = useQuery({ queryKey: ["cal", start], queryFn: async () => (await api.get(`/bookings?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`)).data });
  const { data: custs } = useQuery({ queryKey: ["customers-lite"], queryFn: async () => (await api.get("/customers?limit=200")).data });
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data });

  const all = entries(data?.items || []);
  const [fullDay, setFullDay] = useState(() => localStorage.getItem("pawkt_cal_fullday") === "1");
  const toggleFull = () => { localStorage.setItem("pawkt_cal_fullday", fullDay ? "0" : "1"); setFullDay(!fullDay); };
  const usedHours = all.map((x) => dayjs(x.start).hour());
  const hFrom = fullDay ? 0 : Math.min(DEFAULT_FROM, ...usedHours);
  const hTo = fullDay ? 23 : Math.max(DEFAULT_TO, ...usedHours);
  const HOURS = Array.from({ length: hTo - hFrom + 1 }, (_, i) => i + hFrom);
  const outside = usedHours.filter((h) => h < DEFAULT_FROM || h > DEFAULT_TO).length;
  const forSlot = (day, hour) => all.filter((x) => { const s = dayjs(x.start); return s.isSame(day, "day") && s.hour() === hour; });
  // Mobile day/agenda: same server-authoritative week data, filtered to the focused day.
  const dayItems = all
    .filter((x) => dayjs(x.start).isSame(anchor, "day"))
    .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());

  return (
    <div>
      <PageHeader title="Calendar" subtitle={weekStart.format("MMM D") + " – " + weekStart.add(6, "day").format("MMM D, YYYY")}>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="hidden md:inline-flex" onClick={() => setAnchor(anchor.subtract(1, "week"))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(dayjs())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" className="hidden md:inline-flex" onClick={() => setAnchor(anchor.add(1, "week"))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={toggleFull} data-testid="cal-fullday-toggle">{fullDay ? "Business hours" : "Full day"}</Button>
          {canCreate && <Button onClick={() => { setDefaultDate(dayjs().format("YYYY-MM-DD")); setOpen(true); }} className="bg-slate-900 text-white gap-1.5 ml-1" data-testid="cal-add"><Plus className="h-4 w-4" /> Booking</Button>}
        </div>
      </PageHeader>

      {isLoading ? <Loading /> : (
        <>
        {outside > 0 && !fullDay && <div className="hidden md:block mb-2 text-xs text-slate-500" data-testid="cal-outside-note">{outside} service block(s) this week fall outside 8 AM–7 PM — the grid has been extended to show them.</div>}
        <div className="hidden md:block rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-8 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="p-2 text-xs text-slate-400" />
              {days.map((d) => (
                <div key={d.format()} className={`p-2 text-center border-l border-slate-100 ${d.isSame(dayjs(), "day") ? "bg-emerald-50" : ""}`}>
                  <div className="text-xs text-slate-400">{d.format("ddd")}</div>
                  <div className={`font-display font-bold ${d.isSame(dayjs(), "day") ? "text-emerald-600" : "text-slate-800"}`}>{d.format("D")}</div>
                </div>
              ))}
            </div>
            {HOURS.map((h) => (
              <div key={h} className="grid grid-cols-8 border-b border-slate-100 min-h-[64px]">
                <div className="p-2 text-xs text-slate-400 text-right">{dayjs().hour(h).minute(0).format("h A")}</div>
                {days.map((d) => (
                  <div key={d.format() + h} onClick={canCreate ? () => { setDefaultDate(d.format("YYYY-MM-DD")); setOpen(true); } : undefined}
                    data-testid={`cal-slot-${d.format("YYYY-MM-DD")}-${h}`}
                    className={`border-l border-slate-100 p-1 space-y-1 transition-colors ${canCreate ? "hover:bg-slate-50 cursor-pointer" : ""}`}>
                    {forSlot(d, h).map(({ key, b, a, start, end, label, staff, conflict }) => (
                      <div key={key} data-testid={a ? `cal-block-${a.id}` : `cal-event-${b.id}`} onClick={(e) => { e.stopPropagation(); if (a) setPlan(b); else setCtx({ customerId: b.customer_id, petId: b.pet_id, vertical: deriveVertical(b) }); }} className={`rounded-md border px-1.5 py-1 text-[11px] leading-tight cursor-pointer ${STATUS_BG[b.status] || "bg-slate-100 border-slate-200"} ${conflict ? "ring-1 ring-amber-500" : ""}`}>
                        <div className="font-semibold truncate">{dayjs(start).format("h:mm A")}–{dayjs(end).format("h:mm A")} {label}</div>
                        <div className="truncate opacity-80">{b.customer_name}{b.pet_name ? ` · ${b.pet_name}` : ""}</div>
                        {staff && <div className="truncate opacity-70" data-testid={`cal-staff-${key}`}>{staff}</div>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile day / agenda view — primary calendar representation on phones */}
        <div className="md:hidden" data-testid="cal-mobile-agenda">
          <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-2 py-2">
            <Button variant="outline" size="icon" onClick={() => setAnchor(anchor.subtract(1, "day"))} data-testid="cal-day-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-center">
              <div className="text-xs text-slate-400">{anchor.isSame(dayjs(), "day") ? "Today" : anchor.format("dddd")}</div>
              <div className="font-display font-bold text-slate-800">{anchor.format("MMM D, YYYY")}</div>
            </div>
            <Button variant="outline" size="icon" onClick={() => setAnchor(anchor.add(1, "day"))} data-testid="cal-day-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>

          {dayItems.length ? (
            <div className="space-y-2" data-testid="cal-agenda-list">
              {dayItems.map(({ key, b, a, start, label, staff }) => (
                <button key={key} data-testid={a ? `agenda-block-${a.id}` : `agenda-event-${b.id}`} onClick={() => (a ? setPlan(b) : setCtx({ customerId: b.customer_id, petId: b.pet_id, vertical: deriveVertical(b) }))}
                  className="flex w-full items-stretch gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors active:bg-slate-50">
                  <div className="flex w-14 shrink-0 flex-col items-center justify-center border-r border-slate-100 pr-3">
                    <div className="font-display font-bold leading-none text-slate-800">{dayjs(start).format("h:mm")}</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">{dayjs(start).format("A")}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-slate-900">{b.customer_name}{b.pet_name ? ` · ${b.pet_name}` : ""}</div>
                    <div className="truncate text-sm text-slate-600">{label}</div>
                    {staff && <div className="mt-0.5 truncate text-xs text-slate-400">{staff}</div>}
                  </div>
                  <span className={`shrink-0 self-start rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS_BG[b.status] || "bg-slate-100 border-slate-200 text-slate-600"}`}>{(b.status || "").replace(/_/g, " ")}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="cal-agenda-empty">
              <p className="font-display font-semibold text-slate-700">No appointments</p>
              <p className="mt-1 text-sm text-slate-400">Nothing scheduled for {anchor.format("MMM D")}.</p>
              {canCreate && <Button onClick={() => { setDefaultDate(anchor.format("YYYY-MM-DD")); setOpen(true); }} className="mt-4 gap-1.5 bg-slate-900 text-white" data-testid="cal-agenda-add"><Plus className="h-4 w-4" /> New booking</Button>}
            </div>
          )}
        </div>
        </>
      )}

      {open && canCreate && <BookingModal open={open} onOpenChange={setOpen} defaultDate={defaultDate} custs={custs?.items || []} services={services || []} staff={staff || []}
        onCreated={() => { qc.invalidateQueries({ queryKey: ["cal"] }); setOpen(false); }} />}
      {ctx && <ContextPanel open={!!ctx} onOpenChange={(o) => !o && setCtx(null)} customerId={ctx.customerId} petId={ctx.petId} vertical={ctx.vertical} />}
      {plan && <StaffPlanDialog bookingId={plan.id} title={`${plan.customer_name}${plan.pet_name ? ` · ${plan.pet_name}` : ""}`} onClose={() => setPlan(null)} />}
    </div>
  );
}
